import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import {
  getNewChoiceValuesAndChoiceValuesAfterSearchIfNewValueIsAll,
  getNewChoiceValuesAndChoiceValuesAfterSearchIfNotAll,
  getNewChoiceValuesAndChoiceValuesAfterSearchIfNotAllAndTypeBoolean,
} from 'crm/_newComponents/FastFilters/helpers';

import { setModifiedFastFilters } from 'crm/_redux/actions';

import FastFilters from './FastFilters';

// 将 modifiedFilters 转换为 URL 查询参数对象
const buildQueryParamsFromModifiedFilters = modifiedFilters => {
  const params = {};
  Object.keys(modifiedFilters).forEach(filterName => {
    const filter = modifiedFilters[filterName];
    if (!filter.isDisplayed) return;
    // eslint-disable-next-line camelcase
    const { query_param: queryParam, inputType } = filter;
    if (!queryParam) return;
    if (inputType === 'inputIntegerChoice' || inputType === 'inputStringChoice') {
      const selected = (filter.choiceValues || [])
        .filter(cv => cv.isSelected && cv.value !== 'All')
        .map(cv => cv.value);
      if (selected.length > 0) params[queryParam] = selected;
    } else if (inputType === 'inputBoolean') {
      const selected = (filter.choiceValues || []).find(cv => cv.isSelected && cv.value !== 'All');
      if (selected) params[queryParam] = selected.value;
    } else if (inputType === 'inputDate') {
      const selected = (filter.choiceValues || []).find(cv => cv.isSelected);
      // 选中 All 时不传此筛选条件
      if (selected && selected.label !== 'justAll' && selected.value && selected.value[0] !== null) {
        params[queryParam] = selected.value;
      }
    } else if (inputType === 'inputRange') {
      if (filter.enteredValues && filter.enteredValues.length > 0) {
        params[queryParam] = filter.enteredValues;
      }
    } else if (inputType === 'inputStr') {
      if (filter.value) params[queryParam] = filter.value;
    }
  });
  return params;
};

const FastFiltersWrapper = ({
  getFastFiltersFunc,
  fastFiltersSelector,
  fastFiltersModifiedSelector,
  customValueOfOneFilter,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const fastFiltersModified = useSelector(fastFiltersModifiedSelector);

  // 将 modifiedFilters 更新同步到 URL，触发自动查询
  const syncFiltersToUrl = updatedFilters => {
    const currentQuery = queryString.parse(location.search);
    // 保留非 fastFilter 参数（page, page_size, trading_date, search, filterId 等）
    const fastFilterQueryParams = Object.keys(updatedFilters)
      // eslint-disable-next-line camelcase
      .map(k => updatedFilters[k].query_param)
      .filter(Boolean);
    const baseQuery = {};
    Object.keys(currentQuery).forEach(k => {
      if (!fastFilterQueryParams.includes(k)) {
        baseQuery[k] = currentQuery[k];
      }
    });
    const newFilterParams = buildQueryParamsFromModifiedFilters(updatedFilters);
    const search = queryString.stringify({
      ...baseQuery,
      ...newFilterParams,
      page: 1,
    });
    sessionStorage.setItem('fastFiltersQuery', search);
    history.replace({ search });
  };

  const onChange = (filterName, inputType, newValue) => {
    if (inputType === 'inputRange') {
      const currentFilter = fastFiltersModified[filterName];
      const newFilters = {
        ...fastFiltersModified,
        [filterName]: {
          ...currentFilter,
          enteredValues: newValue,
        },
      };
      dispatch(setModifiedFastFilters(newFilters));
      syncFiltersToUrl(newFilters);
      return;
    }
    if (inputType === 'inputStr') {
      const currentFilter = fastFiltersModified[filterName];
      const newFilters = {
        ...fastFiltersModified,
        [filterName]: {
          ...currentFilter,
          value: newValue,
        },
      };
      dispatch(setModifiedFastFilters(newFilters));
      syncFiltersToUrl(newFilters);
      return;
    }
    if (inputType === 'inputDate') {
      const currentFilter = fastFiltersModified[filterName];

      const newChoiceValues = fastFiltersModified[filterName].choiceValues.map(choiceValue => {
        if (choiceValue.label === newValue.label) {
          return { ...choiceValue, value: newValue.value, isSelected: true };
        }
        return { ...choiceValue, isSelected: false };
      });

      const newFilters = {
        ...fastFiltersModified,
        [filterName]: {
          ...currentFilter,
          choiceValues: newChoiceValues,
        },
      };
      dispatch(setModifiedFastFilters(newFilters));
      syncFiltersToUrl(newFilters);
    }
    if (inputType === 'inputIntegerChoice' || inputType === 'inputStringChoice' || inputType === 'inputBoolean') {
      if (newValue === 'All') {
        const currentFilter = fastFiltersModified[filterName];
        const { choiceValues, choiceValuesAfterSearch } =
          getNewChoiceValuesAndChoiceValuesAfterSearchIfNewValueIsAll(currentFilter);

        const newFilters = {
          ...fastFiltersModified,
          [filterName]: {
            ...currentFilter,
            choiceValues,
            choiceValuesAfterSearch,
          },
        };
        dispatch(setModifiedFastFilters(newFilters));
        syncFiltersToUrl(newFilters);
      } else {
        const currentFilter = fastFiltersModified[filterName];

        const { choiceValues, choiceValuesAfterSearch } =
          inputType !== 'inputBoolean'
            ? getNewChoiceValuesAndChoiceValuesAfterSearchIfNotAll(currentFilter, newValue)
            : getNewChoiceValuesAndChoiceValuesAfterSearchIfNotAllAndTypeBoolean(currentFilter, newValue);

        const newFilters = {
          ...fastFiltersModified,
          [filterName]: {
            ...currentFilter,
            choiceValues,
            choiceValuesAfterSearch,
          },
        };
        dispatch(setModifiedFastFilters(newFilters));
        syncFiltersToUrl(newFilters);
      }
    }
  };

  useEffect(() => {
    onChange(customValueOfOneFilter);
  }, [customValueOfOneFilter]);

  return (
    <FastFilters
      getFastFiltersFunc={getFastFiltersFunc}
      fastFiltersSelector={fastFiltersSelector}
      fastFiltersModified={fastFiltersModified}
      onChange={onChange}
    />
  );
};

FastFiltersWrapper.propTypes = {
  getFastFiltersFunc: PropTypes.object.isRequired,
  fastFiltersSelector: PropTypes.func.isRequired,
  fastFiltersModifiedSelector: PropTypes.func.isRequired,
};

export default FastFiltersWrapper;
